// src/pages/FAQ.jsx
import React, { useState } from "react";
import {
  FaChevronDown,
  FaChevronUp,
  FaPhone,
  FaEnvelope,
  FaFileAlt,
  FaClock,
  FaShip,
  FaPlane,
  FaGlobe,
  FaTruck,
  FaDollarSign,
  FaBox,
} from "react-icons/fa";
import "./FAQ.css";

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqCategories = [
    {
      title: "Documentation & Requirements",
      icon: <FaFileAlt />,
      questions: [
        {
          question: "What documents are required to ship to Kenya?",
          answer:
            "You'll need a commercial invoice, packing list, IDF (Import Declaration Form), Bill of Lading/Air Waybill, and product-specific permits (e.g., KEBS/PPB). Our team guides you through the entire documentation process to ensure compliance.",
        },
        {
          question: "Do you handle export shipping from Kenya?",
          answer:
            "Yes, we provide comprehensive export services including bookings, AWB/BL processing, export entry documentation, and certificates of origin/inspection where applicable.",
        },
      ],
    },
    {
      title: "Shipping & Transit Times",
      icon: <FaClock />,
      questions: [
        {
          question: "How fast do air shipments clear at JKIA?",
          answer:
            "With complete documents and compliant goods, most air consignments clear within 1–3 working days. We expedite the process with our licensed customs expertise.",
        },
        {
          question: "How long does sea freight take?",
          answer:
            "Typical ocean transits to Mombasa are approximately 25–40 days depending on origin, carrier, and routing. We provide accurate ETAs and proactive milestone updates.",
        },
        {
          question: "Sea vs air: which is right for my cargo?",
          answer:
            "Choose air for speed/urgency and sea for bulky or cost-sensitive freight. We'll compare transit time vs. landed cost and recommend the best option for your specific needs.",
        },
      ],
    },
    {
      title: "Services & Operations",
      icon: <FaGlobe />,
      questions: [
        {
          question: "Do you operate at Mombasa, JKIA and ICD Nairobi?",
          answer:
            "Yes, we handle port/airport processes at Mombasa Port, JKIA and ICD Nairobi, plus inland transport and last-mile delivery across Kenya.",
        },
        {
          question: "Can you handle special cargo or permits?",
          answer:
            "Absolutely! We specialize in pharmaceuticals, perishables, mining equipment, and oversize cargo. We coordinate inspections and permits with KEBS/PPB and other relevant agencies.",
        },
        {
          question: "Do you deliver to door?",
          answer:
            "Yes, we arrange countrywide delivery for containers and loose cargo with secure, GPS-tracked movements from port to your doorstep.",
        },
      ],
    },
    {
      title: "Costs & Pricing",
      icon: <FaDollarSign />,
      questions: [
        {
          question: "What taxes & levies apply?",
          answer:
            "Costs include duty (based on HS code), VAT, and government levies such as IDF and RDL. We calculate these precisely for your specific commodity.",
        },
        {
          question: "Can you estimate my landed cost?",
          answer:
            "Yes! Send us your invoice, packing list and HS code; we'll return a comprehensive duty/VAT/levy estimate with all logistics costs included.",
        },
        {
          question: "FCL vs LCL—what's the difference?",
          answer:
            "FCL = full container (faster handling, lower risk per unit). LCL = shared container (cost-effective for small volumes; allow consolidation time). We advise on the best option.",
        },
      ],
    },
  ];

  const quickContacts = [
    {
      icon: <FaPhone />,
      title: "Call Us",
      description: "Speak directly with our logistics experts",
      action: "+254 723 335043",
      link: "tel:+254723335043",
      color: "#10B981",
    },
    {
      icon: <FaEnvelope />,
      title: "Email Us",
      description: "Get detailed quotes and information",
      action: "info@falmouth.co.ke",
      link: "mailto:info@falmouth.co.ke",
      color: "#3B82F6",
    },
    {
      icon: <FaClock />,
      title: "Response Time",
      description: "We typically respond within",
      action: "1-2 hours",
      link: null,
      color: "#F59E0B",
    },
  ];

  return (
    <div className="faq-page">
      {/* Hero Section */}
      <section className="faq-hero">
        <div className="container">
          <div className="hero-content">
            <h1>Frequently Asked Questions</h1>
            <p>
              Find quick answers to common questions about our logistics and
              customs clearance services in Kenya
            </p>
          </div>
        </div>
      </section>

      {/* Quick Contact Cards */}
      <section className="quick-contact">
        <div className="container">
          <div className="contact-cards">
            {quickContacts.map((contact, index) => (
              <div key={index} className="contact-card">
                <div
                  className="contact-icon"
                  style={{ backgroundColor: contact.color }}
                >
                  {contact.icon}
                </div>
                <div className="contact-info">
                  <h3>{contact.title}</h3>
                  <p>{contact.description}</p>
                  {contact.link ? (
                    <a href={contact.link} className="contact-action">
                      {contact.action}
                    </a>
                  ) : (
                    <span className="contact-action">{contact.action}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="faq-categories">
        <div className="container">
          <div className="section-header">
            <h2>Common Questions by Category</h2>
            <p>
              Browse through our most frequently asked questions organized by
              topic
            </p>
          </div>

          <div className="categories-grid">
            {faqCategories.map((category, categoryIndex) => (
              <div key={categoryIndex} className="category-section">
                <div className="category-header">
                  <div className="category-icon">{category.icon}</div>
                  <h3>{category.title}</h3>
                </div>

                <div className="faq-list">
                  {category.questions.map((faq, faqIndex) => {
                    const index = `${categoryIndex}-${faqIndex}`;
                    const isOpen = openIndex === index;

                    return (
                      <div key={index} className="faq-item">
                        <button
                          className="faq-question"
                          onClick={() => toggleFAQ(index)}
                          aria-expanded={isOpen}
                        >
                          <span>{faq.question}</span>
                          {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                        </button>

                        <div className={`faq-answer ${isOpen ? "open" : ""}`}>
                          <p>{faq.answer}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="faq-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Still Have Questions?</h2>
            <p>
              Can't find what you're looking for? Our logistics experts are here
              to help you with any specific questions about your shipment.
            </p>

            <div className="cta-actions">
              <a href="tel:+254723335043" className="btn btn-primary">
                <FaPhone /> Call Our Experts
              </a>
              <a
                href="mailto:info@falmouth.co.ke"
                className="btn btn-secondary"
              >
                <FaEnvelope /> Send Email
              </a>
            </div>

            <div className="cta-info">
              <div className="info-item">
                <FaClock className="info-icon" />
                <span>Mon-Fri: 8:00 AM - 6:00 PM</span>
              </div>
              <div className="info-item">
                <FaClock className="info-icon" />
                <span>Sat: 9:00 AM - 2:00 PM</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
